import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

const AuthContext = createContext({})

// Rate limiting for auth attempts
const authAttempts = {
  signIn: [],
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000 // 15 minutes
};

const checkRateLimit = (type) => {
  const now = Date.now();
  const attempts = authAttempts?.[type] || [];
  
  // Remove old attempts outside the time window
  const recentAttempts = attempts?.filter(time => now - time < authAttempts?.windowMs);
  authAttempts[type] = recentAttempts;
  
  if (recentAttempts?.length >= authAttempts?.maxAttempts) {
    const oldestAttempt = Math.min(...recentAttempts);
    const waitTime = Math.ceil((authAttempts?.windowMs - (now - oldestAttempt)) / 1000 / 60);
    return { allowed: false, waitTime };
  }
  
  return { allowed: true };
};

const recordAttempt = (type) => {
  if (!authAttempts?.[type]) authAttempts[type] = [];
  authAttempts?.[type]?.push(Date.now());
};

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // Use maybeSingle() instead of single() to handle missing profiles gracefully
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle()
        if (!error) {
          if (data) {
            setUserProfile(data)
            logger?.debug('User profile loaded', { userId });
          } else {
            // Profile doesn't exist yet - this is OK, it will be created by triggerlogger?.debug('User profile not found (will be created by trigger)', { userId });
            setUserProfile(null);
          }
        } else {
          logger?.error('Failed to load user profile', error, { userId });
        }
      } catch (error) {
        logger?.error('Profile load error', error, { userId });
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      // Handle email confirmation events
      if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
        logger?.audit('EMAIL_CONFIRMED', session?.user?.id, {
          email: session?.user?.email
        });
      }
      
      // Session rotation on sign in
      if (event === 'SIGNED_IN') {
        logger?.audit('USER_SIGNED_IN', session?.user?.id, {
          email: session?.user?.email
        });
      }
      
      if (event === 'SIGNED_OUT') {
        logger?.audit('USER_SIGNED_OUT', user?.id);
      }
      
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        // Defer profile load by one tick to ensure the Supabase client has
        // fully persisted the session token to localStorage before making
        // authenticated REST requests (prevents 401 Unauthorized errors)
        setTimeout(() => {
          profileOperations?.load(session?.user?.id)
        }, 0)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signUp = async (email, password, metadata = {}) => {
    try {
      const redirectUrl = `${window.location?.origin}/auth/confirm`;
      
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: redirectUrl
        }
      })
      
      if (error) {
        // ENHANCED ERROR LOGGING: Capture full error details
        logger?.warn('Sign up failed', { 
          email, 
          errorMessage: error?.message,
          errorCode: error?.code,
          errorStatus: error?.status,
          errorDetails: error?.details,
          errorHint: error?.hint,
          fullError: JSON.stringify(error)
        });
        
        // Log to console for immediate debugging
        console.error('🔴 SIGNUP ERROR DETAILS:', {
          message: error?.message,
          code: error?.code,
          status: error?.status,
          details: error?.details,
          hint: error?.hint,
          fullError: error
        });
      } else {
        logger?.audit('SIGN_UP_SUCCESS', data?.user?.id, { email });
      }
      
      return { data, error }
    } catch (error) {
      // ENHANCED CATCH LOGGING: Capture exception details
      logger?.error('Sign up error', error, { 
        email,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack
      });
      
      console.error('🔴 SIGNUP EXCEPTION:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        fullError: error
      });
      
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signIn = async (email, password) => {
    // Check rate limiting
    const rateLimit = checkRateLimit('signIn');
    if (!rateLimit?.allowed) {
      logger?.warn('Sign in rate limit exceeded', { email });
      return { 
        error: { 
          message: `Too many sign in attempts. Please try again in ${rateLimit?.waitTime} minutes.` 
        } 
      };
    }
    
    try {
      recordAttempt('signIn');
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      if (error) {
        logger?.warn('Sign in failed', { email, error: error?.message });
      } else {
        logger?.audit('SIGN_IN_SUCCESS', data?.user?.id, { email });
      }
      
      return { data, error }
    } catch (error) {
      logger?.error('Sign in error', error, { email });
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const userId = user?.id;
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
        logger?.audit('SIGN_OUT_SUCCESS', userId);
      } else {
        logger?.error('Sign out failed', error, { userId });
      }
      return { error }
    } catch (error) {
      logger?.error('Sign out error', error);
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      // Map frontend user_type values to database enum values
      const mappedUpdates = { ...updates };
      if (mappedUpdates?.user_type) {
        const userTypeMap = {
          'parent': 'caregiver',
          'child': 'caregiver',
          'elderly': 'elderly',
          'chronic': 'patient',
          'general': 'patient'
        };
        mappedUpdates.user_type = userTypeMap?.[mappedUpdates?.user_type] || mappedUpdates?.user_type;
      }
      
      const { data, error } = await supabase?.from('user_profiles')?.update(mappedUpdates)?.eq('id', user?.id)?.select()?.maybeSingle()
      if (!error) {
        if (data) {
          setUserProfile(data)
          logger?.audit('PROFILE_UPDATED', user?.id, { fields: Object.keys(mappedUpdates) });
        }
      } else {
        logger?.error('Profile update failed', error, { userId: user?.id });
      }
      return { data, error }
    } catch (error) {
      logger?.error('Profile update error', error, { userId: user?.id });
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const resetPasswordForEmail = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email)
      
      if (error) {
        logger?.warn('Password reset request failed', { email, error: error?.message });
      } else {
        logger?.audit('PASSWORD_RESET_REQUESTED', null, { email });
      }
      
      return { data, error }
    } catch (error) {
      logger?.error('Password reset error', error, { email });
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      })
      
      if (error) {
        logger?.warn('Password update failed', { error: error?.message });
      } else {
        logger?.audit('PASSWORD_UPDATED', user?.id);
      }
      
      return { data, error }
    } catch (error) {
      logger?.error('Password update error', error);
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const deleteAccount = async () => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const userId = user?.id;
      const userEmail = user?.email;
      const userName = userProfile?.first_name || 'User';
      const deletionTimestamp = new Date()?.toISOString();
      
      // Send account deletion confirmation email
      try {
        const { error: emailError } = await supabase?.functions?.invoke('send-account-deletion-confirmation', {
          body: {
            userEmail,
            userName,
            deletionTimestamp
          }
        });
        
        if (emailError) {
          logger?.warn('Failed to send deletion confirmation email', { userId, error: emailError });
          // Continue with deletion even if email fails
        } else {
          logger?.audit('DELETION_CONFIRMATION_EMAIL_SENT', userId, { email: userEmail });
        }
      } catch (emailError) {
        logger?.warn('Deletion confirmation email exception', emailError, { userId });
        // Continue with deletion even if email fails
      }
      
      // Delete user account - Supabase will cascade delete related data via database triggers
      const { error } = await supabase?.auth?.admin?.deleteUser(userId);
      
      if (error) {
        logger?.error('Account deletion failed', error, { userId });
        return { error };
      }
      
      // Log successful deletion
      logger?.audit('ACCOUNT_DELETED_SUCCESS', userId, { email: userEmail });
      
      // Sign out and clear local state
      await signOut();
      
      return { error: null };
    } catch (error) {
      logger?.error('Delete account error', error, { userId: user?.id });
      return { error: { message: 'Failed to delete account. Please try again.' } };
    }
  }

  const resendVerificationEmail = async (email) => {
    try {
      const redirectUrl = `${window.location?.origin}/auth/confirm`;
      
      const { data, error } = await supabase?.auth?.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: redirectUrl
        }
      })
      
      if (error) {
        logger?.warn('Resend verification email failed', { email, error: error?.message });
      } else {
        logger?.audit('VERIFICATION_EMAIL_RESENT', null, { email });
      }
      
      return { data, error }
    } catch (error) {
      logger?.error('Resend verification email error', error, { email });
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    updatePassword,
    resetPasswordForEmail,
    resendVerificationEmail,
    deleteAccount
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
